import { DiscardModal } from '@/components/Modals/DiscardModal'
import { useOrganization } from '@/contexts/useOrganization'
import {
  Button,
  CloseButton,
  Dialog,
  Drawer,
  Field,
  HStack,
  Heading,
  IconButton,
  Input,
  List,
  Portal,
  Tag,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { OrgFeaturesSsoConnections } from './OrgFeaturesSsoConnections'

// Utility to compare domain arrays (order-insensitive)
const areDomainsChanged = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return true
  }
  return [...a].sort().join(',') !== [...b].sort().join(',')
}

export type OrgFeaturesSsoProps = {
  canRead?: boolean
  canEdit?: boolean
}

type SsoState = {
  enabled: boolean
  domain_aliases: string[]
}

/**
 * SSO on the org's Features card: one line saying whether it is on, and a
 * Configure button that opens the whole configuration in a drawer.
 *
 * Two independent actions live in that drawer, and they are deliberately not
 * one form:
 *
 * - Domain aliases have an explicit view/edit mode with their own Save and
 *   Cancel. Read is the common case, so the default state is a plain list with
 *   nothing to mis-click; the delete buttons and the add field only exist once
 *   you have said you are editing.
 * - Turning SSO on or off is a single decision, so it is a single button in
 *   the drawer footer rather than a toggle you then have to remember to save.
 *   Turning it *off* locks members out of their usual sign-in, so that one
 *   asks for confirmation first.
 */
export const OrgFeaturesSso = ({
  canRead = true,
  canEdit = true,
}: OrgFeaturesSsoProps) => {
  const { t } = useTranslation('features')

  const orgContext = useOrganization()

  const { orgFeatures } = orgContext

  const { data: features, isLoading, isError } = orgFeatures

  const [savedSso, setSavedSso] = useState<SsoState | null>(null)
  /** Non-null exactly while the domain list is in edit mode. */
  const [draftDomains, setDraftDomains] = useState<string[] | null>(null)
  const [domainInput, setDomainInput] = useState('')
  const [isDrawerOpen, setDrawerOpen] = useState(false)
  /**
   * What the operator was trying to do when we stopped them to ask about the
   * draft — leaving edit mode, or leaving the drawer entirely. Confirming has
   * to finish that action, so the modal has to remember which one it was.
   */
  const [discardIntent, setDiscardIntent] = useState<'edit' | 'drawer' | null>(
    null,
  )
  const [showDisableModal, setShowDisableModal] = useState(false)

  // Initialize local state from fetched SSO data
  useEffect(() => {
    if (features) {
      setSavedSso({
        enabled: Boolean(features.sso?.enabled),
        domain_aliases: features.sso?.domain_aliases || [],
      })
    }
  }, [features])

  const isEnabled = Boolean(savedSso?.enabled)
  const savedDomains = savedSso?.domain_aliases ?? []
  const isEditingDomains = draftDomains !== null
  const visibleDomains = draftDomains ?? savedDomains

  // A typed-but-not-yet-added domain counts as an edit: Save picks it up, so
  // discarding without asking would throw away something the operator typed.
  const domainsChanged =
    isEditingDomains &&
    (areDomainsChanged(draftDomains, savedDomains) ||
      domainInput.trim().length > 0)

  const isMutationDisabled = isLoading || isError || !canEdit

  const patchSso = (next: SsoState) =>
    orgContext.patchOrganisationFeatures({ sso: next }).then(() => {
      setSavedSso(next)
    })

  const startEditingDomains = () => {
    setDraftDomains([...savedDomains])
    setDomainInput('')
  }

  const stopEditingDomains = () => {
    setDraftDomains(null)
    setDomainInput('')
  }

  const handleAddDomain = () => {
    const domain = domainInput.trim()
    if (!domain || draftDomains === null) {
      return
    }
    // Adding a domain already in the list would render two identical rows and
    // send a duplicate to the API; silently keeping one is the honest outcome.
    if (!draftDomains.includes(domain)) {
      setDraftDomains([...draftDomains, domain])
    }
    setDomainInput('')
  }

  const handleRemoveDomain = (domain: string) => {
    if (draftDomains === null) {
      return
    }
    setDraftDomains(draftDomains.filter((d) => d !== domain))
  }

  const handleSaveDomains = () => {
    if (draftDomains === null) {
      return
    }
    // Fold in whatever is sitting in the input — the operator typed it and
    // pressed Save, which is a clear enough intent to add it.
    const pending = domainInput.trim()
    const domain_aliases =
      pending && !draftDomains.includes(pending)
        ? [...draftDomains, pending]
        : draftDomains

    void patchSso({ enabled: isEnabled, domain_aliases }).then(
      stopEditingDomains,
    )
  }

  const handleCancelDomains = () => {
    if (domainsChanged) {
      setDiscardIntent('edit')
      return
    }
    stopEditingDomains()
  }

  const confirmDiscard = () => {
    stopEditingDomains()
    if (discardIntent === 'drawer') {
      setDrawerOpen(false)
    }
    setDiscardIntent(null)
  }

  const handleToggleEnabled = () => {
    if (isEnabled) {
      setShowDisableModal(true)
      return
    }
    void patchSso({ enabled: true, domain_aliases: savedDomains })
  }

  const confirmDisable = () => {
    setShowDisableModal(false)
    void patchSso({ enabled: false, domain_aliases: savedDomains })
  }

  /**
   * Closing the drawer mid-edit would drop the draft, so it runs through the
   * same guard the Cancel button does — the X, the backdrop and Escape all
   * land here.
   */
  const requestDrawerClose = () => {
    if (domainsChanged) {
      setDiscardIntent('drawer')
      return
    }
    stopEditingDomains()
    setDrawerOpen(false)
  }

  const status = isEnabled
    ? t('organization.features.status.enabled')
    : t('organization.features.status.disabled')

  const statusTag = (
    <Tag.Root
      size="sm"
      colorPalette={isEnabled ? 'green' : 'gray'}
      aria-label={status}
    >
      <Tag.Label>{status}</Tag.Label>
    </Tag.Root>
  )

  const readOnlyTag = !canEdit ? (
    <Tooltip.Root positioning={{ placement: 'top' }}>
      <Tooltip.Trigger>
        <Tag.Root size="sm" colorPalette="orange">
          <Tag.Label>{t('organization.features.readOnly.tag')}</Tag.Label>
        </Tag.Root>
      </Tooltip.Trigger>
      <Tooltip.Positioner>
        <Tooltip.Content>
          {t('organization.features.readOnly.tooltip')}
        </Tooltip.Content>
      </Tooltip.Positioner>
    </Tooltip.Root>
  ) : null

  const disableMessages = t(
    'organization.features.sso.disableConfirm.message',
    {
      returnObjects: true,
    },
  ) as string[]

  return (
    <>
      <HStack justify="space-between" gap={3} py={3}>
        <HStack gap={2} flexWrap="wrap">
          <Text fontWeight="medium">
            {t('organization.features.sso.title')}
          </Text>
          {statusTag}
          {readOnlyTag}
        </HStack>
        <Button
          size="sm"
          variant="subtle"
          onClick={() => setDrawerOpen(true)}
          disabled={!canRead || isLoading || isError}
        >
          {t('organization.features.actions.configure')}
        </Button>
      </HStack>

      <Drawer.Root
        open={isDrawerOpen}
        onOpenChange={(details) => {
          if (details.open) {
            setDrawerOpen(true)
            return
          }
          requestDrawerClose()
        }}
        size="lg"
      >
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.Header>
                <HStack gap={2} flexWrap="wrap">
                  <Drawer.Title>
                    {t('organization.features.sso.title')}
                  </Drawer.Title>
                  {statusTag}
                  {readOnlyTag}
                  {domainsChanged && (
                    <Tag.Root
                      size="sm"
                      colorPalette="orange"
                      aria-label={String(
                        t('organization.features.unsavedChangesAria'),
                      )}
                    >
                      <Tag.Label>
                        {t('organization.features.unsavedChanges')}
                      </Tag.Label>
                    </Tag.Root>
                  )}
                </HStack>
                <Drawer.CloseTrigger asChild>
                  <CloseButton />
                </Drawer.CloseTrigger>
              </Drawer.Header>

              <Drawer.Body>
                <VStack alignItems="stretch" gap={6}>
                  <VStack alignItems="stretch" gap={3}>
                    <HStack justify="space-between" gap={3}>
                      <Heading as="h4" fontSize="md" m={0}>
                        {t(
                          'organization.features.sso.settings.domainAliases.label',
                        )}
                      </Heading>
                      {/* Edit only appears in view mode — in edit mode its
                          slot belongs to Save and Cancel, which sit at the
                          bottom of the section with the fields they commit. */}
                      {!isEditingDomains && (
                        <Button
                          size="xs"
                          variant="subtle"
                          onClick={startEditingDomains}
                          disabled={isMutationDisabled}
                        >
                          {t('organization.features.actions.edit')}
                        </Button>
                      )}
                    </HStack>

                    {visibleDomains.length > 0 ? (
                      // `ps` leaves room for the marker, which `list-style-
                      // position: outside` draws in the padding.
                      <List.Root variant="marker" listStyleType="disc" ps={5}>
                        {visibleDomains.map((domain) => (
                          <List.Item key={domain}>
                            {/* The row layout lives on a wrapper, not on the
                                <li>: `display: flex` on a list item replaces
                                `display: list-item`, and the disc goes with
                                it. Inline-flex keeps the item a list item and
                                still gives us a right-aligned delete button. */}
                            <HStack
                              display="inline-flex"
                              width="100%"
                              justify="space-between"
                              gap={2}
                              minH={7}
                            >
                              <Text fontSize="sm">{domain}</Text>
                              {isEditingDomains && (
                                <IconButton
                                  aria-label={`${String(
                                    t(
                                      'organization.features.sso.settings.domainAliases.removeDomain',
                                    ),
                                  )} ${domain}`}
                                  size="xs"
                                  variant="ghost"
                                  colorPalette="red"
                                  onClick={() => handleRemoveDomain(domain)}
                                  disabled={isMutationDisabled}
                                >
                                  <X size={14} />
                                </IconButton>
                              )}
                            </HStack>
                          </List.Item>
                        ))}
                      </List.Root>
                    ) : (
                      <Text fontSize="sm" color="fg.muted">
                        {t(
                          'organization.features.sso.settings.domainAliases.empty',
                        )}
                      </Text>
                    )}

                    {isEditingDomains && (
                      <Field.Root>
                        <HStack width="100%">
                          <Input
                            size="sm"
                            value={domainInput}
                            onChange={(e) => setDomainInput(e.target.value)}
                            placeholder={
                              t(
                                'organization.features.sso.settings.domainAliases.addDomainPlaceholder',
                              ) || ''
                            }
                            onKeyDown={(e) => {
                              if (
                                e.key === 'Enter' &&
                                domainInput.trim() &&
                                !isMutationDisabled
                              ) {
                                e.preventDefault()
                                handleAddDomain()
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            colorPalette="brand"
                            variant="solid"
                            onClick={handleAddDomain}
                            disabled={!domainInput.trim() || isMutationDisabled}
                          >
                            {t('organization.features.actions.add')}
                          </Button>
                        </HStack>
                        <Field.HelperText>
                          {t(
                            'organization.features.sso.settings.domainAliases.domainInputHelper',
                          )}
                        </Field.HelperText>
                      </Field.Root>
                    )}

                    {isEditingDomains && (
                      <HStack gap={2}>
                        <Button
                          size="sm"
                          colorPalette="brand"
                          variant="solid"
                          onClick={handleSaveDomains}
                          disabled={isMutationDisabled}
                        >
                          {t('organization.features.actions.save')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelDomains}
                        >
                          {t('organization.features.actions.cancel')}
                        </Button>
                      </HStack>
                    )}
                  </VStack>

                  <VStack alignItems="stretch" gap={2}>
                    <Heading as="h4" fontSize="md" m={0}>
                      {t('organization.features.sso.connections')}
                    </Heading>
                    <OrgFeaturesSsoConnections
                      orgId={orgContext.selectedOrganizationId}
                      enabled={isDrawerOpen}
                    />
                  </VStack>
                </VStack>
              </Drawer.Body>

              <Drawer.Footer>
                {/* One decision, one button — not a toggle you then have to
                    remember to save. It commits on click. */}
                <Button
                  size="sm"
                  colorPalette={isEnabled ? 'red' : 'brand'}
                  variant={isEnabled ? 'subtle' : 'solid'}
                  onClick={handleToggleEnabled}
                  disabled={isMutationDisabled}
                >
                  {isEnabled
                    ? t('organization.features.sso.disableButton')
                    : t('organization.features.sso.enableButton')}
                </Button>
              </Drawer.Footer>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>

      <DiscardModal
        isOpen={discardIntent !== null}
        onClose={() => setDiscardIntent(null)}
        onConfirmDiscard={confirmDiscard}
      />

      <Dialog.Root
        open={showDisableModal}
        onOpenChange={(details) => !details.open && setShowDisableModal(false)}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>
                  {t('organization.features.sso.disableConfirm.title')}
                </Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                <VStack align="start" gap={3}>
                  {disableMessages.map((message) => (
                    <Text fontSize="md" key={message}>
                      {message}
                    </Text>
                  ))}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button
                  variant="ghost"
                  onClick={() => setShowDisableModal(false)}
                >
                  {t('organization.features.actions.cancel')}
                </Button>
                <Button colorPalette="red" onClick={confirmDisable}>
                  {t('organization.features.sso.disableButton')}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  )
}
